<template>
  <div class="robot-list">
    <!-- 页面标题栏 - 欢迎横幅样式 -->
    <div class="welcome-banner">
      <div class="banner-left">
        <h2 class="banner-title">数字员工管理</h2>
        <p class="banner-sub">当前共 {{ filteredList.length }} 名数字员工</p>
      </div>
      <div class="banner-right">
        <el-button type="success" icon="el-icon-plus" @click="showCreateDialog" class="create-btn">新建机器人</el-button>
      </div>
    </div>

    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <!-- 搜索框 -->
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索机器人名称/账号..."
          prefix-icon="el-icon-search"
          clearable
          @keyup.enter.native="handleSearch"
        />
      </div>
      
      <!-- 角色筛选 -->
      <div class="filter-item">
        <span class="filter-label">角色：</span>
        <el-select v-model="roleFilter" placeholder="全部角色" clearable @change="handleFilterChange">
          <el-option label="全部角色" value="" />
          <el-option v-for="role in roleOptions" :key="role.value" :label="role.label" :value="role.value" />
        </el-select>
      </div>
      
      <!-- 状态筛选 -->
      <div class="filter-item">
        <span class="filter-label">状态：</span>
        <el-select v-model="statusFilter" placeholder="全部状态" clearable @change="handleFilterChange">
          <el-option label="全部状态" value="" />
          <el-option v-for="status in statusOptions" :key="status.value" :label="status.label" :value="status.value" />
        </el-select>
      </div>

      <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
      <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
      
      <!-- 视图模式切换 -->
      <div class="mode-switch">
        <el-button-group>
          <el-button 
            :type="displayMode === 'card' ? 'primary' : 'default'"
            size="small"
            @click="changeMode('card')"
          >
            <i class="el-icon-s-grid"></i> 卡片
          </el-button>
          <el-button 
            :type="displayMode === 'office' ? 'primary' : 'default'"
            size="small"
            @click="changeMode('office')"
          >
            <i class="el-icon-office-building"></i> 办公室
          </el-button>
        </el-button-group>
      </div>
      
      <!-- 全屏按钮 -->
      <div class="fullscreen-btn">
        <el-button type="info" size="small" @click="toggleFullscreen">
          <i :class="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
        </el-button>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper" v-loading="loading">
    </div>
    
    <!-- 空状态 -->
    <div v-else-if="filteredList.length === 0" class="empty-wrapper">
      <el-empty description="暂无机器人数据" />
    </div>
    
    <!-- 卡片模式展示 -->
    <div v-else-if="displayMode === 'card'" class="card-container">
      <div 
        v-for="robot in paginatedList" 
        :key="robot.robot_id"
        class="robot-card"
      >
        <div class="robot-avatar" @click="goToDetail(robot.robot_id)">
          <img v-if="robotAvatars[robot.robot_id]" :src="robotAvatars[robot.robot_id]" :alt="robot.robot_name" />
          <div v-else class="avatar-placeholder" :style="{ background: getAvatarGradient(robot.robot_name) }">
            {{ robot.robot_name.charAt(0) }}
          </div>
          <span :class="['status-dot', getStatusClass(robot.realtime_status)]" :title="getStatusText(robot.realtime_status)"></span>
        </div>
        <div class="robot-info" @click="goToDetail(robot.robot_id)">
          <h3 class="robot-name">{{ robot.robot_name }}</h3>
          <p class="robot-role">{{ robot.robot_role_name || robot.robot_role || '未设置角色' }}</p>
          <div class="robot-meta">
            <el-tag :type="getStatusTagType(robot.realtime_status)" size="mini">{{ getStatusText(robot.realtime_status) }}</el-tag>
            <el-tag v-if="robot.status === 0" type="info" size="mini" effect="plain">已禁用</el-tag>
          </div>
        </div>
        <div class="robot-actions">
          <el-button type="text" size="small" @click.stop="goToDetail(robot.robot_id)">查看</el-button>
          <el-button type="text" size="small" @click.stop="handleEdit(robot)">编辑</el-button>
          <el-button type="text" size="small" class="danger" @click.stop="handleDelete(robot)">删除</el-button>
        </div>
      </div>
    </div>
    
    <!-- 虚拟办公室模式展示 -->
    <div v-else-if="displayMode === 'office'" ref="officeContainer" class="office-container">
      <div class="office-floor">
        <!-- 办公室标题 -->
        <div class="office-header">
          <div class="office-title">
            <i class="el-icon-office-building"></i>
            <span>智能办公区 - {{ filteredList.length }} 数字人在岗</span>
          </div>
          <div class="office-stats">
            <span class="stat-item online">
              <i class="el-icon-success"></i> 在线 {{ onlineCount }}
            </span>
            <span class="stat-item idle">
              <i class="el-icon-circle-check"></i> 空闲 {{ idleCount }}
            </span>
            <span class="stat-item working">
              <i class="el-icon-loading"></i> 工作中 {{ workingCount }}
            </span>
            <span class="stat-item busy">
              <i class="el-icon-time"></i> 忙碌中 {{ busyCount }}
            </span>
            <span class="stat-item offline">
              <i class="el-icon-remove"></i> 离线 {{ offlineCount }}
            </span>
          </div>
        </div>
        
        <!-- 工位区域 -->
        <div class="office-desks-area">
          <div 
            v-for="(robot, index) in paginatedList" 
            :key="robot.robot_id"
            class="office-workstation"
            :class="['status-' + getStatusClass(robot.realtime_status), { 'has-video': robotVideos[robot.robot_id] }]"
            @click="goToDetail(robot.robot_id)"
          >
            <!-- 工位编号 -->
            <div class="desk-number">A{{ String(index + 1).padStart(2, '0') }}</div>
            
            <!-- 办公桌 -->
            <div class="desk-surface">
              <!-- 电脑屏幕 -->
              <div class="computer-monitor">
                <div class="screen-content">
                  <video 
                    v-if="robotVideos[robot.robot_id]" 
                    :src="robotVideos[robot.robot_id]" 
                    class="screen-video"
                    autoplay
                    loop
                    muted
                    playsinline
                  ></video>
                  <template v-else>
                    <img v-if="robotAvatars[robot.robot_id]" :src="robotAvatars[robot.robot_id]" :alt="robot.robot_name" />
                    <div v-else class="screen-avatar" :style="{ background: getAvatarGradient(robot.robot_name) }">
                      {{ robot.robot_name.charAt(0) }}
                    </div>
                  </template>
                </div>
                <div class="monitor-stand"></div>
                <div class="monitor-base"></div>
              </div>
              
              <!-- 键盘 -->
              <div class="keyboard"></div>
              
              <!-- 鼠标垫 -->
              <div class="mouse-pad">
                <div class="mouse"></div>
              </div>
              
              <!-- 咖啡杯 -->
              <div class="coffee-cup" v-if="index % 3 === 0">
                <div class="steam"></div>
              </div>
              
              <!-- 绿植 -->
              <div class="plant" v-if="index % 4 === 1">
                <div class="pot"></div>
                <div class="leaf"></div>
              </div>
            </div>
            
            <!-- 员工信息牌 -->
            <div class="employee-card">
              <div class="employee-name">{{ robot.robot_name }}</div>
              <div class="employee-role">{{ robot.robot_role_name || robot.robot_role || '数字员工' }}</div>
              <div class="employee-status">
                <span :class="['status-badge', getStatusClass(robot.realtime_status)]">
                  {{ getStatusText(robot.realtime_status) }}
                </span>
              </div>
            </div>
            
            <!-- 状态指示灯 -->
            <div class="status-light" :class="getStatusClass(robot.realtime_status)"></div>
          </div>
        </div>
        
        <!-- 办公室装饰 - 地毯纹理 -->
        <div class="office-carpet"></div>
      </div>
    </div>
    
    <!-- 分页 -->
    <div v-if="pagination.total > 0" class="pagination-wrapper">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        :page-size="pagination.pageSize"
        :current-page="pagination.currentPage"
        :page-sizes="[12, 24, 48, 96]"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <!-- 新建/编辑机器人对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form :model="robotForm" :rules="robotRules" ref="robotForm" label-width="100px">
        <el-form-item label="机器人名称" prop="robot_name">
          <el-input v-model="robotForm.robot_name" placeholder="请输入机器人名称"></el-input>
        </el-form-item>
        <el-form-item label="机器人账号" prop="robot_account">
          <el-input v-model="robotForm.robot_account" placeholder="请输入机器人账号"></el-input>
        </el-form-item>
        <el-form-item label="角色" prop="robot_role">
          <el-select v-model="robotForm.robot_role" placeholder="请选择角色" style="width: 100%;">
            <el-option v-for="role in roleOptions" :key="role.value" :label="role.label" :value="role.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="robot_desc">
          <el-input type="textarea" v-model="robotForm.robot_desc" placeholder="请输入描述" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="数字人视频" prop="robot_video">
          <el-input v-model="robotForm.robot_video" placeholder="请输入视频文件名或路径"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRobotForm" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRobotListPaged, getRobotAvatar, getRobotVideo, createRobot, updateRobot, deleteRobot } from '../api/robot'

export default {
  name: 'RobotList',
  data() {
    return {
      loading: false,
      robotList: [], // 机器人列表
      searchKeyword: '',
      roleFilter: '',
      statusFilter: '',
      displayMode: localStorage.getItem('robot_display_mode') || 'card',
      isFullscreen: false,
      robotAvatars: {}, // 存储机器人头像 Base64 数据
      robotVideos: {}, // 存储机器人视频URL
      
      // 分页配置
      pagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0
      },
      
      // 对话框配置
      dialogVisible: false,
      dialogTitle: '新建机器人',
      submitLoading: false,
      isEdit: false,
      editRobotId: null,
      robotForm: {
        robot_name: '',
        robot_account: '',
        robot_role: '',
        robot_desc: '',
        robot_video: ''
      },
      robotRules: {
        robot_name: [{ required: true, message: '请输入机器人名称', trigger: 'blur' }],
        robot_account: [{ required: true, message: '请输入机器人账号', trigger: 'blur' }],
        robot_role: [{ required: true, message: '请选择角色', trigger: 'change' }]
      },
      
      // 角色选项
      roleOptions: [
        { label: '产品经理', value: 'product_manager' },
        { label: '开发工程师', value: 'developer' },
        { label: '测试工程师', value: 'tester' },
        { label: '设计师', value: 'designer' },
        { label: '运营分析师', value: 'operator' },
        { label: '文案策划', value: 'copywriter' },
        { label: '数据分析师', value: 'data_analyst' }
      ],
      
      // 状态选项（realtime_status: 0-离线, 1-在线空闲, 2-工作中, 3-忙碌中）
      statusOptions: [
        { label: '离线', value: '0' },
        { label: '在线空闲', value: '1' },
        { label: '工作中', value: '2' },
        { label: '忙碌中', value: '3' }
      ],
      
      // 状态配置
      statusConfig: {
        0: { text: '离线', className: 'status-offline', tagType: 'info', color: '#909399' },
        1: { text: '在线空闲', className: 'status-idle', tagType: 'success', color: '#67C23A' },
        2: { text: '工作中', className: 'status-working', tagType: 'warning', color: '#E6A23C' },
        3: { text: '忙碌中', className: 'status-busy', tagType: 'primary', color: '#409EFF' }
      },
      
      // 头像渐变色配置 - 匹配Dashboard配色
      avatarGradients: [
        'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
        'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
        'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
        'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
        'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)',
        'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
        'linear-gradient(135deg, #eab308 0%, #facc15 100%)'
      ]
    }
  },

  computed: {
    // 过滤后的数据（前端筛选）
    filteredList() {
      let list = this.robotList || []
      
      // 角色筛选
      if (this.roleFilter) {
        list = list.filter(robot => robot.robot_role === this.roleFilter)
      }
      
      return list
    },
    
    // 分页后的数据
    paginatedList() {
      return this.filteredList
    },
    
    // 在线人数统计（realtime_status=1,2,3）
    onlineCount() {
      return this.filteredList.filter(r => r.realtime_status === 1 || r.realtime_status === 2 || r.realtime_status === 3).length
    },

    // 空闲人数统计（realtime_status=1）
    idleCount() {
      return this.filteredList.filter(r => r.realtime_status === 1).length
    },

    // 工作中人数统计（realtime_status=2）
    workingCount() {
      return this.filteredList.filter(r => r.realtime_status === 2).length
    },

    // 忙碌中人数统计（realtime_status=3）
    busyCount() {
      return this.filteredList.filter(r => r.realtime_status === 3).length
    },

    // 离线人数统计（realtime_status=0 或 null）
    offlineCount() {
      return this.filteredList.filter(r => !r.realtime_status || r.realtime_status === 0).length
    }
  },
  methods: {
    // 加载机器人列表（后端分页）
    async loadRobotList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize,
          keyword: this.searchKeyword || undefined,
          status: this.statusFilter !== '' ? parseInt(this.statusFilter) : undefined
        }
        const res = await getRobotListPaged(params)
        this.robotList = res.list || []
        this.pagination.total = res.total || 0
        // 并行加载机器人头像和视频
        this.loadRobotAvatars()
        this.loadRobotVideos()
      } catch (error) {
        this.$message.error('加载机器人列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    // 加载机器人头像（并行）
    async loadRobotAvatars() {
      const avatarPromises = this.robotList
        .filter(robot => robot.robot_avatar)
        .map(robot => 
          getRobotAvatar(robot.robot_id)
            .then(res => {
              if (res.avatar_base64) {
                this.$set(this.robotAvatars, robot.robot_id, res.avatar_base64)
              }
            })
            .catch(error => {
              console.warn(`加载机器人 ${robot.robot_id} 头像失败:`, error)
            })
        )
      await Promise.all(avatarPromises)
    },
    
    // 加载机器人视频（并行）
    async loadRobotVideos() {
      const videoPromises = this.robotList
        .filter(robot => robot.robot_video)
        .map(robot => 
          getRobotVideo(robot.robot_id)
            .then(res => {
              if (res.video_url) {
                this.$set(this.robotVideos, robot.robot_id, res.video_url)
              }
            })
            .catch(error => {
              // 404表示没有视频，这是正常的
              if (error.response && error.response.status !== 404) {
                console.warn(`加载机器人 ${robot.robot_id} 视频失败:`, error)
              }
            })
        )
      await Promise.all(videoPromises)
    },
    
    // 获取头像渐变色
    getAvatarGradient(name) {
      if (!name) return this.avatarGradients[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      const index = Math.abs(hash) % this.avatarGradients.length
      return this.avatarGradients[index]
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.loadRobotList()
    },
    
    // 筛选条件变化
    handleFilterChange() {
      this.pagination.currentPage = 1
      this.loadRobotList()
    },
    
    // 重置筛选
    resetFilters() {
      this.searchKeyword = ''
      this.roleFilter = ''
      this.statusFilter = ''
      this.pagination.currentPage = 1
      this.loadRobotList()
    },
    
    // 切换展示模式
    changeMode(mode) {
      this.displayMode = mode
      localStorage.setItem('robot_display_mode', mode)
    },
    
    // 分页变化
    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadRobotList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    
    // 每页条数变化
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadRobotList()
    },
    
    // 获取状态样式类名
    getStatusClass(status) {
      return this.statusConfig[status]?.className || 'status-offline'
    },
    
    // 获取状态文本
    getStatusText(status) {
      return this.statusConfig[status]?.text || '未知'
    },
    
    // 获取状态Tag类型
    getStatusTagType(status) {
      return this.statusConfig[status]?.tagType || 'info'
    },
    
    // 跳转详情页
    goToDetail(robot_id) {
      this.$router.push({ name: 'RobotDetail', params: { id: robot_id } })
    },
    
    // 显示新建对话框
    showCreateDialog() {
      this.isEdit = false
      this.editRobotId = null
      this.dialogTitle = '新建机器人'
      this.robotForm = {
        robot_name: '',
        robot_account: '',
        robot_role: '',
        robot_desc: '',
        robot_video: ''
      }
      this.dialogVisible = true
    },
    
    // 编辑机器人
    handleEdit(robot) {
      this.isEdit = true
      this.editRobotId = robot.robot_id
      this.dialogTitle = '编辑机器人'
      this.robotForm = {
        robot_name: robot.robot_name,
        robot_account: robot.robot_account,
        robot_role: robot.robot_role,
        robot_desc: robot.robot_desc || '',
        robot_video: robot.robot_video || ''
      }
      this.dialogVisible = true
    },
    
    // 删除机器人
    async handleDelete(robot) {
      try {
        await this.$confirm(`确定要删除机器人 "${robot.robot_name}" 吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteRobot(robot.robot_id)
        this.$message.success('删除成功')
        this.loadRobotList()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败: ' + (error.message || '未知错误'))
        }
      }
    },
    
    // 提交表单
    async submitRobotForm() {
      this.$refs.robotForm.validate(async (valid) => {
        if (!valid) return
        
        this.submitLoading = true
        try {
          if (this.isEdit) {
            await updateRobot(this.editRobotId, this.robotForm)
            this.$message.success('更新成功')
          } else {
            await createRobot(this.robotForm)
            this.$message.success('创建成功')
          }
          this.dialogVisible = false
          this.loadRobotList()
        } catch (error) {
          this.$message.error(this.isEdit ? '更新失败' : '创建失败')
          console.error(error)
        } finally {
          this.submitLoading = false
        }
      })
    },
    
    // 切换全屏（仅在办公室模式下对办公区全屏）
    toggleFullscreen() {
      // 只有在办公室模式下才对办公区全屏
      if (this.displayMode === 'office') {
        const officeEl = this.$refs.officeContainer
        if (!officeEl) return

        if (!document.fullscreenElement) {
          officeEl.requestFullscreen().then(() => {
            this.isFullscreen = true
            officeEl.classList.add('is-fullscreen')
          }).catch(err => {
            this.$message.error('全屏模式失败: ' + err.message)
          })
        } else {
          document.exitFullscreen().then(() => {
            this.isFullscreen = false
            officeEl.classList.remove('is-fullscreen')
          }).catch(err => {
            this.$message.error('退出全屏失败: ' + err.message)
          })
        }
      } else {
        // 卡片模式下对整个页面全屏（保持原有行为）
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().then(() => {
            this.isFullscreen = true
          }).catch(err => {
            this.$message.error('全屏模式失败: ' + err.message)
          })
        } else {
          document.exitFullscreen().then(() => {
            this.isFullscreen = false
          }).catch(err => {
            this.$message.error('退出全屏失败: ' + err.message)
          })
        }
      }
    }
  },
  
  mounted() {
    this.loadRobotList()
    // 监听全屏变化事件
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
    })
  },
  
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
    })
  }
}
</script>

<style lang="scss" scoped>
.robot-list {
  padding: 16px;
  min-height: calc(100vh - 84px);
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

/* 欢迎横幅 */
.welcome-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);
}

.banner-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.banner-sub {
  margin: 0 0 0 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  display: inline;
}

.banner-left {
  display: flex;
  align-items: center;
}

.banner-right {
  display: flex;
  align-items: center;
}

.create-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  i { margin-right: 4px; }
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.search-box {
  width: 220px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-label {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.filter-item :deep(.el-select) {
  width: 140px;
}

.mode-switch {
  margin-left: auto;
}

.fullscreen-btn {
  margin-left: var(--spacing-xs);
}

/* 卡片容器 */
.card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

/* 机器人卡片 */
.robot-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
}

.robot-avatar {
  position: relative;
  margin-bottom: var(--spacing-md);
  overflow: visible;

  img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
  }
}

.avatar-placeholder {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: var(--text-white);
  background: var(--primary-gradient);
  border-radius: var(--radius-full);
}

.robot-avatar .status-dot {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
  border: 3px solid var(--bg-card) !important;
  box-sizing: border-box;
  display: block;
  z-index: 10;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  overflow: hidden;
  /* 重置可能被覆盖的样式 */
  padding: 0 !important;
  align-items: normal !important;
  gap: normal !important;
  font-size: 0 !important;
  font-weight: normal !important;
  color: transparent !important;
  background: none !important;
}

.robot-avatar .status-dot.status-idle {
  background-color: #10b981 !important;
}

.robot-avatar .status-dot.status-working {
  background-color: #f97316 !important;
}

.robot-avatar .status-dot.status-busy {
  background-color: #6366f1 !important;
}

.robot-avatar .status-dot.status-offline {
  background-color: #909399 !important;
}

.robot-avatar .status-dot.status-maintenance {
  background-color: #ef4444 !important;
}

.robot-info {
  text-align: center;
  cursor: pointer;
  flex: 1;
}

.robot-name {
  margin: 0 0 6px 0;
  font-size: 15px;
  font-weight: 600;
  color: #606266;
}

.robot-role {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #909399;
}

.robot-meta {
  display: flex;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.robot-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
  width: 100%;

  .el-button {
    padding: 0 8px;
    font-size: 12px;
  }
}

.danger {
  color: var(--danger-color);

  &:hover {
    color: var(--danger-light);
  }
}

/* 虚拟办公室模式 */
.office-container {
  margin-bottom: 16px;
}

/* 全屏模式样式 */
.office-container.is-fullscreen {
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  padding: 24px;
  overflow-y: auto;
}

.office-container.is-fullscreen .office-floor {
  min-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.office-container.is-fullscreen .office-desks-area {
  flex: 1;
  min-height: auto;
  gap: 16px;
  padding: 20px;
  align-content: start;
}

.office-floor {
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 办公室头部 */
.office-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);
  color: #fff;
}

.office-title {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.office-title i {
  font-size: 18px;
}

.office-stats {
  display: flex;
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
}

.stat-item.online { color: #fff; }
.stat-item.idle { color: #fff; }
.stat-item.working { color: #fff; }
.stat-item.busy { color: #fff; }
.stat-item.offline { color: rgba(255, 255, 255, 0.7); }

/* 工位区域 */
.office-desks-area {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding: 16px;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  min-height: 400px;
}

/* 单个工位 */
.office-workstation {
  position: relative;
  background: #fff;
  border-radius: 10px;
  padding: 20px 16px 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 280px;
}

/* 全屏模式下的工位卡片 */
.office-container.is-fullscreen .office-workstation {
  padding: var(--spacing-lg) var(--spacing-md) var(--spacing-sm);
  min-height: 280px;
}

/* 全屏模式下的桌面 */
.office-container.is-fullscreen .desk-surface {
  padding: 16px 12px 10px;
  margin-bottom: 10px;
  flex: 1;
  width: 100%;
  min-height: 140px;
}

/* 全屏模式下的显示器 */
.office-container.is-fullscreen .computer-monitor {
  width: 100%;
  height: 100%;
  max-width: 180px;
  max-height: 120px;
  flex: 1;
}

/* 全屏模式下的员工信息 */
.office-container.is-fullscreen .employee-card {
  padding: 8px;
}

.office-container.is-fullscreen .employee-name {
  font-size: 14px;
}

.office-container.is-fullscreen .employee-role {
  font-size: 12px;
  margin-bottom: 8px;
}

.office-workstation:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: #818cf8;
}

.office-workstation.status-online {
  border-left: 4px solid #10b981;
}

.office-workstation.status-working {
  border-left: 4px solid #f97316;
  animation: workingPulse 2s ease-in-out infinite;
}

.office-workstation.status-busy {
  border-left: 4px solid #6366f1;
  animation: busyPulse 2s ease-in-out infinite;
}

.office-workstation.status-offline {
  border-left: 4px solid #909399;
  opacity: 0.7;
}

@keyframes workingPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(230, 162, 60, 0.2); }
  50% { box-shadow: 0 0 0 8px rgba(230, 162, 60, 0); }
}

@keyframes busyPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.2); }
  50% { box-shadow: 0 0 0 8px rgba(64, 158, 255, 0); }
}

/* 工位编号 */
.desk-number {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  font-family: 'Courier New', monospace;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* 办公桌表面 */
.desk-surface {
  position: relative;
  background: linear-gradient(180deg, #e8d5b7 0%, #d4c4a8 100%);
  border-radius: 12px;
  padding: 16px 12px 10px;
  margin-bottom: 10px;
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.05);
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 140px;
}

/* 电脑显示器 */
.computer-monitor {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 180px;
  max-height: 120px;
  margin: 0 auto;
  background: #2c3e50;
  border-radius: 8px;
  padding: 4px;
  box-shadow:
    0 4px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  flex: 1;
}

.screen-content {
  width: 100%;
  height: 100%;
  background: #1a1a2e;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.screen-content img,
.screen-content .screen-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.screen-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: 600;
  color: #fff;
}

.screen-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.monitor-stand {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 8px;
  background: #34495e;
  border-radius: 0 0 4px 4px;
}

.monitor-base {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 4px;
  background: #34495e;
  border-radius: 2px;
}

/* 键盘 */
.keyboard {
  width: 80px;
  height: 12px;
  background: linear-gradient(180deg, #5d6d7e 0%, #4a5568 100%);
  border-radius: 3px;
  margin: 12px auto 0;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.keyboard::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 4px;
  right: 4px;
  height: 2px;
  background: repeating-linear-gradient(
    90deg,
    #718096 0px,
    #718096 4px,
    transparent 4px,
    transparent 6px
  );
}

/* 鼠标垫 */
.mouse-pad {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 24px;
  height: 20px;
  background: #3d3d3d;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.mouse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 14px;
  background: linear-gradient(180deg, #718096 0%, #4a5568 100%);
  border-radius: 5px;
}

/* 咖啡杯 */
.coffee-cup {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 14px;
  height: 12px;
  background: linear-gradient(180deg, #e74c3c 0%, #c0392b 100%);
  border-radius: 0 0 7px 7px;
}

.coffee-cup::before {
  content: '';
  position: absolute;
  top: 2px;
  right: -4px;
  width: 6px;
  height: 6px;
  border: 2px solid #e74c3c;
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.steam {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 6px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: steam 1.5s ease-out infinite;
}

@keyframes steam {
  0% { transform: translateX(-50%) translateY(0); opacity: 0.6; }
  100% { transform: translateX(-50%) translateY(-8px); opacity: 0; }
}

/* 绿植 */
.plant {
  position: absolute;
  bottom: 8px;
  left: 8px;
}

.pot {
  width: 12px;
  height: 10px;
  background: linear-gradient(180deg, #d35400 0%, #a04000 100%);
  border-radius: 0 0 3px 3px;
}

.leaf {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 12px;
  background: linear-gradient(180deg, #27ae60 0%, #1e8449 100%);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
}

/* 员工信息牌 */
.employee-card {
  text-align: center;
  padding: 8px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  width: 100%;
}

.employee-name {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 2px;
}

.employee-role {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.employee-status {
  display: flex;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.status-idle {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.status-badge.status-working {
  background: rgba(249, 115, 22, 0.15);
  color: #f97316;
  animation: badgeBlink 1.5s ease-in-out infinite;
}

.status-badge.status-busy {
  background: rgba(99, 102, 241, 0.15);
  color: #6366f1;
}

.status-badge.status-offline {
  background: rgba(144, 144, 153, 0.15);
  color: #909399;
}

/* 状态指示灯 */
.status-light {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 8px !important;
  height: 8px !important;
  border-radius: 50% !important;
  box-shadow: 0 0 4px currentColor;
  /* 重置可能被覆盖的样式 */
  display: block !important;
  padding: 0 !important;
  align-items: normal !important;
  gap: normal !important;
  font-size: 0 !important;
  font-weight: normal !important;
}

.status-light.status-idle {
  background: #10b981 !important;
  color: #10b981;
}

.status-light.status-working {
  background: #f97316 !important;
  color: #f97316;
  animation: lightBlink 1s ease-in-out infinite;
}

.status-light.status-busy {
  background: #6366f1 !important;
  color: #6366f1;
  animation: lightBlink 1s ease-in-out infinite;
}

.status-light.status-offline {
  background: #909399 !important;
  color: #909399;
}

@keyframes lightBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes badgeBlink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.98); }
}

/* 地毯装饰 */
.office-carpet {
  display: none;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-top: 16px;
}

/* 加载和空状态 */
.loading-wrapper,
.empty-wrapper {
  padding: 60px 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    width: 100%;
  }
  
  .filter-item {
    width: 100%;

    :deep(.el-select) {
      flex: 1;
      width: auto;
    }
  }

  .mode-switch {
    margin-left: 0;
    width: 100%;

    :deep(.el-button-group) {
      display: flex;
      width: 100%;

      .el-button {
        flex: 1;
      }
    }
  }
  
  .card-container {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
  
  .robot-card {
    padding: 16px 12px;
  }
  
  .robot-avatar img,
  .robot-avatar .avatar-placeholder {
    width: 60px;
    height: 60px;
  }
  
  /* 办公室模式响应式 */
  .office-floor {
    padding: 12px;
  }
  
  .office-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .office-stats {
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .office-desks-area {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    padding: 12px;
  }
  
  .computer-monitor {
    width: 90px;
    height: 60px;
  }

  .desk-surface {
    padding: 10px 8px 6px;
  }
  
  .keyboard {
    width: 60px;
    height: 10px;
  }
  
  .employee-name {
    font-size: 13px;
  }
  
  .employee-role {
    font-size: 11px;
  }
}
</style>
